import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/library/card";
import { Separator } from "@/components/ui/separator";
import CallHistoryItem from "./components/CallHistoryItem";
import { SortDropdown } from "@/components/ui/sortDropdown";
import { useCallLogs } from "@/features/leads/api/useCallLogs";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { Loader2 } from "lucide-react";

const STATUS_FILTERS = [
  { label: "All" },
  { label: "ANSWERED" },
  { label: "MISSED" },
];

export default function CallHistory() {
  const [statusFilter, setStatusFilter] = useState("All");
  const { lead } = useLeadForm();

  const { logs, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCallLogs({
      leadId: lead?.id,
      status: statusFilter,
    });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node || !hasNextPage) return;

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="p-4">
      <Card className="py-2">
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h1 className="text-md font-medium">Call History</h1>
            <SortDropdown
              value={statusFilter}
              menuItems={STATUS_FILTERS}
              onSelect={setStatusFilter}
              className="h-[2.5rem]"
            />
          </div>
          <Separator />

          <div className="flex flex-col w-full">
            {isLoading ? (
              <div className="flex items-center justify-center w-full py-8">
                <Loader2 className="size-5 animate-spin text-[#8a918d]" />
              </div>
            ) : logs.length === 0 ? (
              <p className="text-sm text-[#8a918d] py-8 w-full text-center">
                No call history found
              </p>
            ) : (
              <>
                {logs.map((call: any) => (
                  <CallHistoryItem key={call.id} call={call} />
                ))}
                <div ref={sentinelRef} className="h-1 w-full" />
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center w-full py-4">
                    <Loader2 className="size-4 animate-spin text-[#8a918d]" />
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
